import {Badge} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/badge.types';
import {useTranslation} from '@aboutyou/i18n';
import {BadgeKind} from '@aboutyou/ui-components';
import {useMemo} from 'react';

type DisplayBadgesConfigType = {
  isInBasket: boolean;
  personalized: 'favorite' | 'oftenBought' | 'recommended' | null;
  sale: string | null;
  campaignSale: string | null;
  useDefaultLabels?: boolean;
  showPriceOverhaulV2SaleLabel: boolean;
};

/**
 * Fills the values for personalized placeholder badges, campaign, sale and inBasket badges
 */
export const useDisplayBadges = (
  badges: Badge[],
  {
    isInBasket,
    personalized,
    sale,
    campaignSale,
    useDefaultLabels,
    showPriceOverhaulV2SaleLabel,
  }: DisplayBadgesConfigType,
): BadgeKind[] => {
  const {t} = useTranslation();

  return useMemo(() => {
    // eslint-disable-next-line complexity
    const calculated = badges.map(badge => {
      switch (badge.type?.$case) {
        case 'productAttribute': {
          return {
            kind: 'GENERIC',
            label: badge.type.productAttribute.label,
            textColor: badge.type.productAttribute.textColor,
            backgroundColor: badge.type.productAttribute.backgroundColor,
            untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
          } as BadgeKind;
        }
        case 'personalizedBrand': {
          if (!personalized) {
            return null;
          }
          let label: string;
          let untranslatedLabel: string;
          switch (personalized) {
            case 'favorite':
              label = t('Favourite brands');
              untranslatedLabel = 'favouriteBrands';
              break;
            case 'oftenBought':
              label = t('Frequently purchased');
              untranslatedLabel = 'frequentlyPurchased';
              break;
            case 'recommended':
              label = t('Our recommendation');
              untranslatedLabel = 'ourRecommendation';
              break;
            default:
              label = '';
              untranslatedLabel = '';
          }

          return {
            kind: 'PERSONALIZED',
            variation: personalized,
            label,
            untranslatedLabel,
          } as BadgeKind;
        }
        case 'campaignSale': {
          if (useDefaultLabels) {
            return {
              kind: 'CAMPAIGN_SALE',
              color: badge.type.campaignSale.campaignColor,
              label: badge.type.campaignSale.defaultLabel,
              untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
            } as BadgeKind;
          }

          if (campaignSale) {
            return {
              kind: 'CAMPAIGN_SALE',
              color: badge.type.campaignSale.campaignColor,
              label: campaignSale || badge.type.campaignSale.defaultLabel,
              untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
            } as BadgeKind;
          }

          return null;
        }
        case 'sale': {
          if (showPriceOverhaulV2SaleLabel) {
            return {
              kind: 'SALE',
              label: t('Sale').toUpperCase(),
              untranslatedLabel: 'sale',
            } as BadgeKind;
          }

          if (useDefaultLabels) {
            return {
              kind: 'SALE',
              label: badge.type.sale.defaultLabel,
              untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
            } as BadgeKind;
          }

          if (sale) {
            return {
              kind: 'SALE',
              label: sale || badge.type.sale.defaultLabel,
              untranslatedLabel: formatTrackingLabel(
                sale || badge.tracker.contextKey,
              ),
            } as BadgeKind;
          }
          return null;
        }
        case 'inBasket': {
          return isInBasket
            ? ({
                kind: 'IN_BASKET',
                label: t('In the basket'),
                untranslatedLabel: 'inBasket',
              } as BadgeKind)
            : null;
        }
        case 'upcyclingFashion': {
          return {
            kind: 'UPCYCLING',
            label: badge.type.upcyclingFashion.label,
            untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
          } as BadgeKind;
        }
        case 'couponIconBadge': {
          return {
            kind: 'COUPON',
            label: badge.type.couponIconBadge.label,
            backgroundColor: badge.type.couponIconBadge.backgroundColor.hex,
            foregroundColor: badge.type.couponIconBadge.foregroundColor.hex,
            untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
          } as BadgeKind;
        }
        case 'couponDisabled': {
          return {
            kind: 'COUPON_DISABLED',
            label: badge.type.couponDisabled.label,
            untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
          } as BadgeKind;
        }
        case 'couponTextBadge': {
          return {
            kind: 'COUPON',
            label: badge.type.couponTextBadge.label,
            untranslatedLabel: formatTrackingLabel(badge.tracker.contextKey),
          } as BadgeKind;
        }
        default: {
          return null;
        }
      }
    });

    return calculated.filter((badge): badge is BadgeKind => !!badge);
  }, [
    badges,
    campaignSale,
    isInBasket,
    personalized,
    sale,
    t,
    useDefaultLabels,
  ]);
};

// backend adds the prefix "product.badges." to the contextKey string which is unnecessary
// also converts snake_case to camelCase
const formatTrackingLabel = (str?: string) => {
  if (!str) return str;

  const cleanedStr = str.includes('product.badges.')
    ? str.replace('product.badges.', '')
    : str;

  // Convert snake_case to camelCase
  return cleanedStr.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

// reducer to obtain the product id and the brand id from the badges array
// done mainly to match with the badges on the product ADP slider
export const getBrandIdAndProductId = (badges: Badge[]) => {
  return badges.reduce(
    (
      acc: {brandId: number | undefined; productId: number | undefined},
      badge: Badge,
    ) => {
      if (badge.type?.$case === 'personalizedBrand') {
        acc.brandId = badge.type.personalizedBrand.brandId;
      }
      if (badge.type?.$case === 'inBasket') {
        acc.productId = badge.type.inBasket.productId;
      }
      return acc;
    },
    {brandId: undefined, productId: undefined},
  );
};

export const getBadgeNameList = (badges: BadgeKind[]) => {
  const calculated = badges.map(badge => {
    switch (badge.kind) {
      case 'GENERIC':
      case 'PERSONALIZED':
      case 'CAMPAIGN_SALE':
      case 'SALE':
      case 'IN_BASKET':
      case 'UPCYCLING':
      case 'COUPON':
      case 'COUPON_DISABLED':
        return badge.untranslatedLabel;
      default:
        return null;
    }
  }) as Array<string | null>;

  return calculated.filter(badge => !!badge) as string[];
};
